import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  BaseAccordian,
  LargeButton,
  OverlayLoader,
  Ptag,
} from "components/core";
import http from "services/httpService";
import {
  exitPlanSetupOrPending,
  formatToPascalCase,
  invokePendingPlanSetup,
  ivnvokePlanSetup,
} from "utlis/utils";
import { DashboardWrapper, Footer, Header } from "./styles";
import routes from "components/routes";
import { INSURANCE_PRODUCTS } from "constants";
import { includes } from "lodash";
import { colors, theme } from "theme";
import { FLEX_PRODUCTS } from "constants";
import { PRIORITY_ORDER } from "constants";
import { dumpStorage, getCompanyInfo, setupInitLocalStorage } from "./utils";
import DashboardHeader from "./DashboardHeader";
import DoneIcon from "@mui/icons-material/Done";
import useUser from "hooks/useUser";

function MainDashboard() {
  const name = localStorage.getItem("name");
  const { setUser } = useUser("admin");
  const user_id = localStorage.getItem("user_id");
  const permission = localStorage.getItem("permission");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState(); // storing companies list information

  useEffect(() => {
    (async function () {
      await fetchCompaniesList();
      
      // Only dump storage if not coming from a completed plan setup
      const isPlanSetupCompleted = localStorage.getItem("plan_setup_completed") === "true";
      if (!isPlanSetupCompleted) {
        dumpStorage();
      } else {
        // Clear the plan setup completion flag after successful navigation to dashboard
        localStorage.removeItem("plan_setup_completed");
        localStorage.removeItem("plan_setup_completed_timestamp");
      }
    })();
  }, []);

  const totalClients = useMemo(() => {
    if (!companies) return 0;
    return Object.keys(companies["GMC"].active).length;
  }, [companies]);

  const clientInProgress = useMemo(() => {
    if (!companies) return 0;
    return Object.keys(companies["GMC"]).reduce((a, c) => {
      if (c === "active") return a;
      return a + Object.keys(companies["GMC"][c]).length;
    }, 0);
  }, [companies]);

  // fetching companies list information
  const fetchCompaniesList = async () => {
    try {
      let { data } = await http.get("business/get_companies/", {
        params: {
          user_id: localStorage.getItem("user_id"),
        },
      });
      setCompanies(data);
    } catch {
      alert("Some error has occured!");
    } finally {
      setLoading(false);
    }
  };

  const getProductsNames = (arr, flex) => {
    const products = [];

    if (flex) {
      const sortedArray = Object.entries(arr)
        .sort(([keyA, valA], [keyB, valB]) => {
          if (valA === valB) {
            const indexA = PRIORITY_ORDER.indexOf(keyA);
            const indexB = PRIORITY_ORDER.indexOf(keyB);
            return indexA - indexB;
          }
          return valA ? -1 : 1;
        })
        .map(([key]) => key);

      sortedArray.forEach((product) => {
        switch (product) {
          case "health_insurance":
            products.push(FLEX_PRODUCTS["health_insurance"]);
            break;
          case "health_insurance_additional":
            products.push(FLEX_PRODUCTS["health_insurance_additional"]);
            break;
          case "personal_accident":
            products.push(FLEX_PRODUCTS["personal_accident"]);
            break;
          case "term_life":
            products.push(FLEX_PRODUCTS["term_life"]);
            break;
          case "mandatory_top_up_health":
            products.push(FLEX_PRODUCTS["mandatory_top_up_health"]);
            break;
          case "top_up_health":
            products.push(FLEX_PRODUCTS["top_up_health"]);
            break;
          case "opd_cover":
            products.push(FLEX_PRODUCTS["opd_cover"]);
            break;
          case "critical_illness":
            products.push(FLEX_PRODUCTS["critical_illness"]);
            break;
          case "health_checkup":
            products.push(FLEX_PRODUCTS["health_checkup"]);
            break;
          case "parental_cover":
            products.push(FLEX_PRODUCTS["parental_cover"]);
            break;
          case "health_insurance_parents":
            products.push(FLEX_PRODUCTS["health_insurance_parents"]);
            break;
          case "health_insurance_parents_in_laws":
            products.push(FLEX_PRODUCTS["health_insurance_parents_in_laws"]);
            break;
        }
      });
    } else {
      const sortedProducts = arr.sort((a, b) => {
        if (a.slice(0, 3) < b.slice(0, 3)) {
          return -1;
        } else if (a.slice(0, 3) > b.slice(0, 3)) {
          return 1;
        } else {
          return a.length < b.length ? -1 : 1;
        }
      });

      sortedProducts.forEach((product) => {
        switch (product) {
          case "GMC":
          case "GMC_M":
            products.push(INSURANCE_PRODUCTS[0]);
            break;
          case "GPA":
            products.push(INSURANCE_PRODUCTS[1]);
            break;
          case "GTL":
            products.push(INSURANCE_PRODUCTS[2]);
            break;
        }
      });
    }
    return products;
  };
  // handling gmc company click
  const handleGmcClick = async (type, status, company, flex) => {
    const companyDetails = companies[type][status][company]; // company details

    // getting token and other info from the API
    const companyInfo = await getCompanyInfo(
      companyDetails.business_user_id,
      companyDetails.company_name
    );
    // returning if there's an error
    if (!companyInfo) return;
    if (status === "pending_setup_employee_data_upload") {
      localStorage.setItem("company_name", companyDetails.company_name);
      localStorage.setItem("token", companyInfo.token);
      localStorage.setItem("id", companyDetails.business_user_id);
      const { data } = await http.get("/business/get_all_tasksets/");
      const { data: dat } = await http.get(
        "/business/get_company_product_details/"
      );
      const flex_userplan_ids = Object.keys(dat.product_details).reduce(
        (acc, key) => {
          acc[key] = dat.product_details[key].userplan;
          return acc;
        },
        {}
      );
      const pageToShow = data[0].page_to_show;
      const superTaskId = data[0].id;

      localStorage.setItem(
        "flex_userplan_ids",
        JSON.stringify(flex_userplan_ids)
      );
      localStorage.setItem("super_task_id", superTaskId);

      switch (pageToShow) {
        case "MASTER_FAILURE":
          navigate({
            pathname: `/${routes.FLEX_ERROR_FILE}`,
          });
          break;
        case "CUTOFF_DATE_PENDING":
        case "MASTER_PENDING":
          navigate({
            pathname: `/${routes.FLEX_PENDING_STATE}`,
          });
          break;
        case "PRODUCTS_NOT_ALL_SUCCESS":
        case "CUTOFF_DATE_INSERT":
          navigate({
            pathname: `/${routes.FLEX_PRODUCT_STATUS}`,
          });
          break;
        case "PRODUCTS_INSERT":
          navigate({
            pathname: `/${routes.FLEX_PRODUCT_UPLOAD}`,
          });
          break;
        case "ALL_SUCCESS":
          navigate({
            pathname: `/${routes.FLEX_DASHBOARD}`,
          });
          break;
      }
      return;
    }
    // populating localStorage

    if (!flex) {
      setupInitLocalStorage(
        "FLEX",
        companyDetails.company_name,
        companyInfo.token,
        companyDetails.business_user_id,
        companyInfo.company.is_approved,
        flex
      );
    } else {
      setupInitLocalStorage(
        "FLEX",
        companyDetails.company_name,
        companyInfo.token,
        companyDetails.business_user_id,
        companyInfo.company.is_approved,
        flex
      );
    }

    // populating localStorage with userplan_id if it exists
    if (companyDetails.user_plan_id && companyDetails.user_plan_id.length) {
      localStorage.setItem("userplan_id", companyDetails.user_plan_id[0]);
    }
    // setting up routes based on status
    setUser(companyInfo);
    let route;
    switch (status) {
      case "pending_setup":
        //navigating benefits page for flex or insuranve setup for original GMC
        if (flex == false) {
          route = routes.INSURANCE_SETUP;
          break;
        } else {
          route = routes.FLEX_BENEFITS_SETUP;
          break;
        }
      case "pending_upload":
        // populating localStorage with products in that company
        if (!flex) {
          const businessType = companyDetails.business_type;
          const gmc_business_type = businessType.includes("GMC_M")
            ? "GMC_M"
            : businessType.includes("GMC")
            ? "GMC"
            : null;

          if (gmc_business_type) {
            localStorage.setItem("gmc_business_type", gmc_business_type);
          }
        }
        localStorage.setItem(
          "selected_plan_document",
          JSON.stringify(getProductsNames(companyDetails.business_type, flex))
        );
        route = routes.SELECT_PLAN;
        break;
      case "active":
        route = "/v1";
        break;
      case "pending_employees_confirmation":
        route = routes.FLEX_DASHBOARD;
    }
    localStorage.setItem("is_product_flex", flex ? 1 : 0); //Differentiation key for flex
    // navigating to computed route with proper params
    console.log({ route });
    navigate("/v1");
    // navigate({
    //   pathname: `/${route}`,
    //   params: {
    //     bus_id: companyDetails.business_user_id,
    //     userplan_id:
    //       companyDetails.user_plan_id &&
    //       companyDetails.user_plan_id.length &&
    //       companyDetails.user_plan_id[0],
    //     company_name: companyDetails.company_name,
    //   },
    // });
  };

  // handling sbo company click
  const handleSboClick = async (type, status, company) => {
    const companyDetails = companies[type][status][company]; // getting company
    // getting token and other info from the API
    const companyInfo = await getCompanyInfo(
      companyDetails.business_user_id,
      companyDetails.company_name
    );
    // returning if there's an error
    if (!companyInfo) return;
    // populating localStorage
    setupInitLocalStorage(
      null,
      companyDetails.company_name,
      companyInfo.token,
      companyDetails.business_user_id
    );

    // populating localStorage with userplan_id if it exists
    if (companyDetails.user_plan_id && companyDetails.user_plan_id.length) {
      localStorage.setItem("userplan_id", companyDetails.user_plan_id[0]);
    }
    // setting up routes based on status
    let route;
    switch (status) {
      case "active":
      case "pending_payment":
        route = routes.CARE_DASHBOARD;
        break;
      case "pending_setup":
        route = "/buildyourplan/onboarding";
        break;
      case "pending_upload":
        route = "/uploadexcel/onboarding";
        break;
    }

    // navigating to computed route with proper params
    navigate({
      pathname: `${route}`,
      params: {
        bus_id: companyDetails.business_user_id,
        id: companyDetails.business_user_id,
        userplan_id:
          companyDetails.user_plan_id &&
          companyDetails.user_plan_id.length &&
          companyDetails.user_plan_id[0],
        company_name: companyDetails.company_name,
      },
    });
  };

  // handling company click
  const handleCompanyClick = async (type, status, company, flex) => {
    if (status === "pending_setup") {
      await invokePendingPlanSetup(true);
      const companyDetails = companies[type][status][company]; // company details
      const companyInfo = await getCompanyInfo(
        companyDetails.business_user_id,
        companyDetails.company_name
      );
      if (!companyInfo) return;
      localStorage.setItem("company_name", companyDetails.company_name);
      localStorage.setItem("token", companyInfo.token);
      localStorage.setItem("id", companyDetails.business_user_id);
      if (flex) {
        localStorage.setItem("global_plan_type", "Flex");
      }
      localStorage.setItem("universal_product", flex);
      navigate(`/${routes.UNIVERSAL_PLAN_SETUP}`, {
        state: { navigation: "dashboardnavigation" },
      });
      // now we are redirection to the universal plan setup page and there we can check for next page to render
      // if (flex) {
      //   navigate(`/${routes.UNIVERSAL_PRODUCT_SELECT_UHID}`);
      // } else {
      //   navigate(`/${routes.UNIVERSAL_PLAN_SETUP}`);
      // }
      return;
    }
    if (status === "active") {
      exitPlanSetupOrPending();
    }
    setLoading(true);
    switch (type) {
      case "GMC":
        await handleGmcClick(type, status, company, flex);
        break;
      case "SBO":
        await handleSboClick(type, status, company);
        break;
    }
    setLoading(false);
  };

  // generating companies list
  const getCompaniesList = (type, status) => {
    const companiesList = companies[type][status];
    return (
      <List>
        {Object.keys(companiesList).map((company, index) => (
          <ListItem key={index.toString()} sx={{ paddingTop: "0px" }}>
            <ListItemButton
              onClick={() =>
                handleCompanyClick(
                  type,
                  status,
                  company,
                  companiesList[company].flex,
                  companiesList[company]
                )
              }
              sx={{
                backgroundColor: "rgb(231, 242, 255)",
                borderRadius: "5px",
                transition: "0.2s all ease-in",
                ":hover": {
                  color: "white",
                  background: theme.primaryColor,
                  transform: "scale(1.01)",
                },
              }}
            >
              <ListItemText
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "14px",
                    fontFamily: "Inter",
                  },
                }}
                primary={companiesList[company].company_name}
              />
              {companiesList[company].is_approved && (
                <Chip
                  label="Active"
                  sx={{
                    borderRadius: "14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    backgroundColor: colors.primaryColor,
                    color: "white",
                    fontFamily: "Inter",
                    fontWeight: "500",
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  const setupCompany = (flow) => {
    switch (flow) {
      case "GCP":
        localStorage.setItem("flow", flow);
        break;
      case "GMC":
        localStorage.setItem("flow", flow);
    }
    ivnvokePlanSetup(true);
    navigate("/company-setup/onboarding");
  };

  const resetOnboardingState = () => {
    localStorage.removeItem("company_setup_draft_onboarding");
    localStorage.removeItem("plan_selection_data");
    localStorage.removeItem("benefits_state");
    localStorage.removeItem("plan_setup_completed");
    localStorage.removeItem("plan_setup_completed_timestamp");
  };

  return (
    <>
      {!loading ? (
        <DashboardWrapper>
          <Header>
            <Ptag
              fontSize="26px"
              letterHeight="44px"
              fontWeight="600"
              fontFamily="Merriweather"
            >
              {name ? `Hello ${name}` : "Welcome to Vital"}
            </Ptag>
          </Header>
          {totalClients || clientInProgress ? (
            <DashboardHeader
              totalClients={totalClients}
              clientInProgress={clientInProgress}
            />
          ) : null}
          <div style={{ margin: "4rem 0rem" }}>
            {/* we are only showing active and pending_setup plans because they are too lazy to do it */}
            {["active", "pending_setup"].map((status, index) => {
              return (
                Object.keys(companies["GMC"][status]).length > 0 && (
                  <BaseAccordian
                    key={index.toString()}
                    title={formatToPascalCase(status)}
                    body={getCompaniesList("GMC", status)}
                  />
                )
              );
            })}
          </div>
          {permission && permission == "CREATE" && (
            <Footer>
              <LargeButton
                fontSize="18px"
                onClick={() =>{
                  resetOnboardingState();
                  // Wait a moment to ensure localStorage operations complete before navigating
                  setTimeout(() => {
                    setupCompany("GMC");
                  }, 100);
                }}
                label="Create Plan"
                type="submit"
              />
            </Footer>
          )}
        </DashboardWrapper>
      ) : (
        <OverlayLoader />
      )}
    </>
  );
}

export default MainDashboard;
